import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Container,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getArticles, useDeleteArticle } from '../queries/articles';
import { getTermsAndConditionsList, useUpdateTermsByType } from '../queries/termsAndConditions';
import ConfirmDialog from '../components/ConfirmDialog';
import { formatDate as formatDisplayDate } from '../utils/helper';

function ContentPage() {
  const navigate = useNavigate();
  const [leadContent, setLeadContent] = useState('');
  const [clientContent, setClientContent] = useState('');
  const [leadSaving, setLeadSaving] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const { data: articles = [], isRefetching } = useQuery(getArticles({}));
  const { data: termsList = [] } = useQuery(getTermsAndConditionsList({}));

  const deleteArticle = useDeleteArticle();
  const updateTermsLead = useUpdateTermsByType();
  const updateTermsClient = useUpdateTermsByType();

  React.useEffect(() => {
    const lead = termsList.find((t) => t.type === 'lead');
    const client = termsList.find((t) => t.type === 'client');
    if (lead) setLeadContent(lead.content ?? '');
    if (client) setClientContent(client.content ?? '');
  }, [termsList]);

  const handleDeleteArticle = async () => {
    if (!selectedArticle) return;
    try {
      await deleteArticle.mutateAsync({ id: selectedArticle.id });
      toast.success('Article deleted');
      setSelectedArticle(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to delete article');
    }
  };

  const handleSaveLeadTerms = async () => {
    setLeadSaving(true);
    try {
      await updateTermsLead.mutateAsync({ type: 'lead', content: leadContent });
      toast.success('Lead Agent Terms & Conditions saved');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save');
    } finally {
      setLeadSaving(false);
    }
  };

  const handleSaveClientTerms = async () => {
    setClientSaving(true);
    try {
      await updateTermsClient.mutateAsync({ type: 'client', content: clientContent });
      toast.success('Client Terms & Conditions saved');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save');
    } finally {
      setClientSaving(false);
    }
  };

  const formatDate = (d) => formatDisplayDate(d, '');

  return (
    <Container maxWidth='lg' sx={{ my: 4 }}>
      <Card variant='outlined' sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.06)', p: 2 }}>
        <CardContent>
          <Typography variant='h5' fontWeight='bold' textTransform='uppercase' gutterBottom>
            Content
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Manage articles and terms & conditions.
          </Typography>

          {/* Articles */}
          <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ mt: 2, mb: 2 }}>
            <Typography variant='h6' fontWeight='bold'>
              Articles
            </Typography>
            <Button variant='contained' startIcon={<AddIcon />} component={Link} to='/content/articles/new'>
              New Article
            </Button>
          </Box>
          {isRefetching ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {articles.length === 0 ? (
                <Typography color='text.secondary'>No articles yet. Create one to get started.</Typography>
              ) : (
                articles.map((article) => (
                  <Card key={article._id} variant='outlined' sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box display='flex' justifyContent='space-between' alignItems='flex-start' sx={{ mb: 0.5 }}>
                        <Box display='flex' alignItems='center' gap={0.5} sx={{ maxWidth: '80%', minWidth: 0 }}>
                          <Typography
                            variant='subtitle1'
                            fontWeight='bold'
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {article.title}
                          </Typography>
                          {article.published && (
                            <CheckCircleIcon
                              sx={{ fontSize: 20, color: 'success.main', flexShrink: 0 }}
                              titleAccess='Published'
                            />
                          )}
                        </Box>
                        <Box>
                          <IconButton
                            aria-label='edit'
                            onClick={() => navigate(`/content/articles/${article._id}`)}
                            size='small'
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            aria-label='delete'
                            onClick={() => setSelectedArticle({ id: article._id, title: article.title })}
                            size='small'
                            color='error'
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography variant='body2' color='text.secondary'>
                        {formatDate(article.date)}
                      </Typography>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}

          <Divider sx={{ my: 4 }} />

          {/* Terms & Conditions */}
          <Typography variant='h6' fontWeight='bold' sx={{ mb: 2 }}>
            Terms & Conditions
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
              Lead Agent Terms & Conditions
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={6}
              value={leadContent}
              onChange={(e) => setLeadContent(e.target.value)}
              placeholder='Enter content for Lead Agent Terms & Conditions...'
              variant='outlined'
              size='small'
              sx={{ mb: 1 }}
            />
            <Button variant='contained' onClick={handleSaveLeadTerms} disabled={leadSaving}>
              {leadSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>

          <Box>
            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
              Client Terms & Conditions
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={6}
              value={clientContent}
              onChange={(e) => setClientContent(e.target.value)}
              placeholder='Enter content for Client Terms & Conditions...'
              variant='outlined'
              size='small'
              sx={{ mb: 1 }}
            />
            <Button variant='contained' onClick={handleSaveClientTerms} disabled={clientSaving}>
              {clientSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!selectedArticle}
        onClose={() => setSelectedArticle(null)}
        title='Delete Article'
        content={selectedArticle ? `Are you sure you want to delete "${selectedArticle.title}"?` : 'Are you sure?'}
        confirmButton='Delete'
        confirmButtonProps={{ color: 'error', variant: 'contained' }}
        loading={deleteArticle.isPending}
        onConfirm={handleDeleteArticle}
      />
    </Container>
  );
}

export default ContentPage;
